import { FC, useCallback, useMemo } from 'react';

import {
  ClientAccountStatusType,
  ClientOnboardingStatusType,
  OnboardingStatusType,
} from 'api/types/entity';
import {
  DialogConfirmDelete,
  DialogConfirmRevokeOtpVerification,
} from 'components';
import { parseFullName } from 'helpers';
import { useTranslation } from 'libs/i18n';
import { useDialog } from 'libs/ui/Dialog/useDialog';
import { ClientFormContent } from 'modules/client/common/components';
import {
  LockAndUnlockReadOnlyProps,
  useLockAndUnlockReadOnly,
} from 'modules/client/common/hooks';

import { ActionProps } from '../../../../common/components/ClientFormContent/types';
import { renderClientStatus } from '../../../../common/helpers';
import { useFormContext } from '../../../forms/clientForm';
import { ClientSections, ClientSectionsProps } from '../../ClientSections';

export interface FormContentProps
  extends LockAndUnlockReadOnlyProps,
    ClientSectionsProps {
  hasRepresentative: boolean;
  submitLoading?: boolean;
  currentOnboardingStatus: ClientOnboardingStatusType;
  currentAccountStatus: ClientAccountStatusType | null;
  revokeOtpVerificationLoading?: boolean;
  deleteLoading?: boolean;
  onChangeOnboardingStatus?: () => void;
  onChangeAccountStatus?: () => void;
  onShowVersionHistory?: () => void;
  onRevokeOtpVerification?: () => Promise<any>;
  onSendInvitationToAccount?: () => void;
  onLinkToBright?: () => void;
  onDownloadClientQuestionnaire?: () => void;
  onDelete?: () => void;
}

export const FormContent: FC<FormContentProps> = ({
  countries,
  countriesLoading,
  hasRepresentative,
  submitLoading,
  kycData,
  onboardingData,
  managers,
  managersLoading,
  currentOnboardingStatus,
  visibleOnboardingStatuses,
  currentAccountStatus,
  revokeOtpVerificationLoading,
  deleteLoading,
  isEditablePersonalAccountNumber,
  mainInfo,
  onChangeOnboardingStatus,
  onChangeAccountStatus,
  lockClientIfPossible,
  unlockClient,
  onShowVersionHistory,
  onRevokeOtpVerification,
  onSendInvitationToAccount,
  onLinkToBright,
  representativeInfo,
  onClickRepresentative,
  onDownloadClientQuestionnaire,
  onDelete,
}) => {
  const { resetForm, initialValues, setReadOnly } = useFormContext();
  const { t } = useTranslation();

  const fullName = initialValues
    ? parseFullName(initialValues.personalData)
    : '';

  const { onDisableReadOnly, onEnableReadOnly } = useLockAndUnlockReadOnly(
    setReadOnly,
    { unlockClient, lockClientIfPossible }
  );

  const {
    isOpen: isOpenRevokeOtp,
    onClose: onCloseRevokeOtp,
    open: openRevokeOtp,
  } = useDialog();

  const {
    isOpen: isOpenConfirmDelete,
    onClose: onCloseConfirmDelete,
    open: openConfirmDelete,
  } = useDialog();

  const status = useMemo(
    () =>
      renderClientStatus(
        {
          onboarding: onboardingData,
          status: currentAccountStatus || currentOnboardingStatus,
        },
        t,
        { size: 's' }
      ),
    [currentAccountStatus, currentOnboardingStatus, onboardingData, t]
  );

  const onActionRevokeOtpVerification = useCallback(() => {
    openRevokeOtp();
  }, [openRevokeOtp]);

  const onConfirmRevokeOtp = useCallback(async () => {
    await onRevokeOtpVerification?.().finally(() => onCloseRevokeOtp());
  }, [onCloseRevokeOtp, onRevokeOtpVerification]);

  const handleDelete = useCallback(() => {
    openConfirmDelete();
  }, [openConfirmDelete]);

  const actionMenuProps = useMemo<ActionProps>(
    () => ({
      onChangeAccountStatus:
        currentOnboardingStatus === OnboardingStatusType.AccountOpened &&
        currentAccountStatus !== ClientAccountStatusType.Closed
          ? onChangeAccountStatus
          : undefined,
      onChangeOnboardingStatus:
        currentOnboardingStatus !== OnboardingStatusType.AccountOpened
          ? onChangeOnboardingStatus
          : undefined,
      onRevokeOtpVerification: onRevokeOtpVerification
        ? onActionRevokeOtpVerification
        : undefined,
      onDownloadClientQuestionnaire,

      onSendInvitationToAccount,
      onLinkToBright,
      onDelete: onDelete ? handleDelete : undefined,
    }),
    [
      currentOnboardingStatus,
      currentAccountStatus,
      onChangeAccountStatus,
      onChangeOnboardingStatus,
      onRevokeOtpVerification,
      onActionRevokeOtpVerification,
      onDownloadClientQuestionnaire,
      onSendInvitationToAccount,
      onLinkToBright,
      onDelete,
      handleDelete,
    ]
  );

  return (
    <>
      <ClientFormContent
        actionMenuProps={actionMenuProps}
        content={
          <ClientSections
            countries={countries}
            countriesLoading={countriesLoading}
            isEditablePersonalAccountNumber={isEditablePersonalAccountNumber}
            kycData={kycData}
            mainInfo={mainInfo}
            managers={managers}
            managersLoading={managersLoading}
            onboardingData={onboardingData}
            representativeInfo={representativeInfo}
            visibleOnboardingStatuses={visibleOnboardingStatuses}
            onClickRepresentative={onClickRepresentative}
          />
        }
        headerAfterContent={status}
        headerTitle={fullName}
        resetForm={resetForm}
        shownTabBar={hasRepresentative}
        submitLoading={submitLoading}
        onEdit={onDisableReadOnly}
        onEnableReadOnly={onEnableReadOnly}
        onShowVersionHistory={onShowVersionHistory}
      />
      <DialogConfirmRevokeOtpVerification
        fullName={fullName}
        isOpen={isOpenRevokeOtp}
        loading={revokeOtpVerificationLoading}
        onClose={onCloseRevokeOtp}
        onConfirm={onConfirmRevokeOtp}
      />
      {onDelete && (
        <DialogConfirmDelete
          isOpen={isOpenConfirmDelete}
          loading={deleteLoading}
          onClose={onCloseConfirmDelete}
          onConfirm={onDelete}
        />
      )}
    </>
  );
};
